import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Upload,
} from 'lucide-react';
import { ROLE_LABELS } from '../../data/mockData';

export default function UserCreateWizardView({
  canCreateUsers,
  creationStep,
  setCreationStep,
  selectedCreationRole,
  allowedRolesToCreate,
  handleStartCreation,
  handleSaveCreatedUser,
  formValues,
  setFormValues,
  currentUserObj,
  currentRole,
  users,
  navigate,
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm max-w-4xl mx-auto">
      {/* Step Guide Bar */}
      <div className="flex items-center justify-center gap-2 mb-6">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${creationStep === 1 ? 'bg-blue-600 text-white shadow-sm' : 'bg-emerald-100 text-emerald-700'}`}>
          {creationStep === 1 ? '1' : <CheckCircle2 className="w-4 h-4 text-emerald-700" />}
        </span>
        <span className="text-xs font-semibold text-slate-500">Select User Role</span>
        <ArrowRight className="w-4 h-4 text-slate-300" />
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${creationStep === 2 ? 'bg-blue-600 text-white shadow-sm' : 'bg-slate-100 text-slate-400'}`}>
          2
        </span>
        <span className="text-xs font-semibold text-slate-500">Dynamic Details Form</span>
      </div>

      {/* STEP 1: SELECT ROLE */}
      {creationStep === 1 && (
        <div className="space-y-6 py-6 text-center max-w-md mx-auto">
          {!canCreateUsers ? (
            <div className="border border-rose-100 bg-rose-50 rounded-xl p-5 text-center">
              <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
              <h3 className="text-sm font-bold text-slate-800">Permission Restricted</h3>
              <p className="text-xs text-slate-500 mt-1">
                Your current role does not have authorization to append any downline users to this system.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <h2 className="text-base font-bold text-slate-800">Select Creator Path Scope</h2>
                <p className="text-xs text-slate-500">
                  Choose a role to initialize. Only valid hierarchy nodes downline are enabled for selection.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5 text-left">
                  <Label htmlFor="create-role" className="font-semibold text-slate-700">Creation Target Role</Label>
                  <Select onValueChange={handleStartCreation}>
                    <SelectTrigger className="bg-slate-50/50 border-slate-200 text-xs py-5">
                      <SelectValue placeholder="Select target role" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {allowedRolesToCreate.map(role => (
                        <SelectItem key={role} value={role}>{ROLE_LABELS[role] || role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="border-t border-slate-100 pt-4 text-left">
                  <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-2">Creator Hierarchy Binding:</p>
                  <div className="bg-slate-50 border border-slate-150 rounded-lg p-3 text-xs text-slate-600 flex items-center justify-between">
                    <span>Parent Node: <strong className="text-slate-800">{currentUserObj.name}</strong></span>
                    <span className="text-[10px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded font-mono font-bold uppercase">{ROLE_LABELS[currentRole]}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* STEP 2: DYNAMIC DETAILS FORM */}
      {creationStep === 2 && (
        <form onSubmit={handleSaveCreatedUser} className="space-y-6">
          {/* Dynamic Header */}
          <div className="bg-slate-50 border border-slate-250 rounded-xl p-4 flex items-center justify-between">
            <div>
              <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-mono font-bold uppercase">Dynamic Form Loaded</span>
              <h3 className="text-sm font-bold text-slate-800 mt-1">Role: {ROLE_LABELS[selectedCreationRole]} specifications</h3>
            </div>
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-800 text-xs" onClick={() => setCreationStep(1)}>
              Change Role
            </Button>
          </div>

          {/* 2-Column Responsive Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* --- UNIVERSAL FIELD SECTIONS --- */}
            {selectedCreationRole !== 'vendor' && selectedCreationRole !== 'client' ? (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Full Name <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="John Doe"
                    value={formValues.fullName || ''}
                    onChange={(e) => setFormValues({...formValues, fullName: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Contact Number <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="+91 99999 99999"
                    value={formValues.contactNumber || ''}
                    onChange={(e) => setFormValues({...formValues, contactNumber: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Email ID <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    type="email"
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="john.doe@company.com"
                    value={formValues.email || ''}
                    onChange={(e) => setFormValues({...formValues, email: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Address <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="House No, Street, Landmark"
                    value={formValues.address || ''}
                    onChange={(e) => setFormValues({...formValues, address: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Emergency Contact Number</Label>
                  <Input
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="+91 99999 88888"
                    value={formValues.emergencyPhone || ''}
                    onChange={(e) => setFormValues({...formValues, emergencyPhone: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Date Of Joining <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    type="date"
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    value={formValues.doj || ''}
                    onChange={(e) => setFormValues({...formValues, doj: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Date Of Birth <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    type="date"
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    value={formValues.dob || ''}
                    onChange={(e) => setFormValues({...formValues, dob: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Status</Label>
                  <Select value={formValues.status} onValueChange={(val) => setFormValues({...formValues, status: val})}>
                    <SelectTrigger className="bg-slate-50/50 border-slate-200 text-xs h-9">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5 col-span-1">
                  <Label className="text-xs font-semibold text-slate-700">Profile Photo</Label>
                  <div className="border border-dashed border-slate-200 rounded-lg p-3 bg-slate-50 text-center hover:bg-slate-100 transition cursor-pointer">
                    <Upload className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                    <span className="text-[10px] text-slate-600 font-medium block">Click to upload JPG/PNG</span>
                  </div>
                </div>

                {selectedCreationRole !== 'universal_qc' && selectedCreationRole !== 'data_entry_operator' && (
                  <div className="space-y-1.5 col-span-1">
                    <Label className="text-xs font-semibold text-slate-700">ID Proof Upload</Label>
                    <div className="border border-dashed border-slate-200 rounded-lg p-3 bg-slate-50 text-center hover:bg-slate-100 transition cursor-pointer">
                      <Upload className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                      <span className="text-[10px] text-slate-600 font-medium block">Click to upload PAN/Aadhaar</span>
                    </div>
                  </div>
                )}
              </>
            ) : null}

            {/* --- SUPREME MANAGER FIELDS --- */}
            {selectedCreationRole === 'supreme_manager' && (
              <div className="space-y-1.5 col-span-1 md:col-span-2">
                <Label className="text-xs font-semibold text-slate-700">Assigned Region <span className="text-rose-500">*</span></Label>
                <Select value={formValues.assignedRegion} onValueChange={(val) => setFormValues({...formValues, assignedRegion: val})}>
                  <SelectTrigger className="bg-slate-50/50 border-slate-200 text-xs h-9">
                    <SelectValue placeholder="Select Region" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="North">North Region</SelectItem>
                    <SelectItem value="West">West Region</SelectItem>
                    <SelectItem value="South">South Region</SelectItem>
                    <SelectItem value="East">East Region</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* --- MANAGER FIELDS --- */}
            {selectedCreationRole === 'manager' && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Assigned Region <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="West-A"
                    value={formValues.assignedRegion || ''}
                    onChange={(e) => setFormValues({...formValues, assignedRegion: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Team Limit Size <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    type="number"
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="25"
                    value={formValues.teamLimit || ''}
                    onChange={(e) => setFormValues({...formValues, teamLimit: e.target.value})}
                  />
                </div>
              </>
            )}

            {/* --- QUALITY CHECKER FIELDS --- */}
            {selectedCreationRole === 'quality_checker' && (
              <div className="space-y-1.5 col-span-1 md:col-span-2">
                <Label className="text-xs font-semibold text-slate-700">Assigned Manager <span className="text-rose-500">*</span></Label>
                <Select value={formValues.assignedManager} onValueChange={(val) => setFormValues({...formValues, assignedManager: val})}>
                  <SelectTrigger className="bg-slate-50/50 border-slate-200 text-xs h-9">
                    <SelectValue placeholder="Select Manager" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {users.filter(u => u.role === 'manager').map(mgr => (
                      <SelectItem key={mgr.id} value={mgr.name}>{mgr.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* --- FIELD EXECUTIVE FIELDS --- */}
            {selectedCreationRole === 'field_executive' && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Aadhaar Card Number <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs font-mono"
                    placeholder="1234 5678 9012"
                    value={formValues.aadhaarNumber || ''}
                    onChange={(e) => setFormValues({...formValues, aadhaarNumber: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">PAN Card Number <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs font-mono uppercase"
                    placeholder="ABCDE1234F"
                    value={formValues.panNumber || ''}
                    onChange={(e) => setFormValues({...formValues, panNumber: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Assigned Area / Pin Code <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Kankurgachi, 700054"
                    value={formValues.assignedArea || ''}
                    onChange={(e) => setFormValues({...formValues, assignedArea: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Assigned Reporting Manager <span className="text-rose-500">*</span></Label>
                  <Select value={formValues.assignedManager} onValueChange={(val) => setFormValues({...formValues, assignedManager: val})}>
                    <SelectTrigger className="bg-slate-50/50 border-slate-200 text-xs h-9">
                      <SelectValue placeholder="Select Manager" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {users.filter(u => u.role === 'manager').map(mgr => (
                        <SelectItem key={mgr.id} value={mgr.name}>{mgr.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Bank Account Details <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs font-mono"
                    placeholder="Account Number"
                    value={formValues.bankDetailsAcc || ''}
                    onChange={(e) => setFormValues({...formValues, bankDetailsAcc: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">IFSC Code <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs font-mono uppercase"
                    placeholder="HDFC0001234"
                    value={formValues.bankDetailsIfsc || ''}
                    onChange={(e) => setFormValues({...formValues, bankDetailsIfsc: e.target.value})}
                  />
                </div>
              </>
            )}

            {/* --- UDEOP FIELDS --- */}
            {selectedCreationRole === 'data_entry_operator' && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Shift Timings <span className="text-rose-500">*</span></Label>
                <Select value={formValues.shiftTiming} onValueChange={(val) => setFormValues({...formValues, shiftTiming: val})}>
                  <SelectTrigger className="bg-slate-50/50 border-slate-200 text-xs h-9">
                    <SelectValue placeholder="Select Shift" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="Morning Shift (06 AM - 02 PM)">Morning Shift (06 AM - 02 PM)</SelectItem>
                    <SelectItem value="General Shift (09 AM - 06 PM)">General Shift (09 AM - 06 PM)</SelectItem>
                    <SelectItem value="Evening Shift (02 PM - 10 PM)">Evening Shift (02 PM - 10 PM)</SelectItem>
                    <SelectItem value="Night Shift (10 PM - 06 AM)">Night Shift (10 PM - 06 AM)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* --- UQC FIELDS --- */}
            {selectedCreationRole === 'universal_qc' && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">QC Level <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Level 2 Expert"
                    value={formValues.qcLevel || ''}
                    onChange={(e) => setFormValues({...formValues, qcLevel: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Assigned Department <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="KYC Verification"
                    value={formValues.assignedDepartment || ''}
                    onChange={(e) => setFormValues({...formValues, assignedDepartment: e.target.value})}
                  />
                </div>
              </>
            )}

            {/* --- VENDOR SIDE FIELD EXECUTIVE FIELDS --- */}
            {selectedCreationRole === 'vendor_fe' && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Vendor Parent Mapping <span className="text-rose-500">*</span></Label>
                  <Select value={formValues.vendorMapping} onValueChange={(val) => setFormValues({...formValues, vendorMapping: val})}>
                    <SelectTrigger className="bg-slate-50/50 border-slate-200 text-xs h-9">
                      <SelectValue placeholder="Select Vendor Partner" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {users.filter(u => u.role === 'vendor').map(v => (
                        <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Assigned Operational City <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Pune, Maharashtra"
                    value={formValues.assignedCity || ''}
                    onChange={(e) => setFormValues({...formValues, assignedCity: e.target.value})}
                  />
                </div>
              </>
            )}

            {/* --- VENDOR AND CLIENT FORMS (COMPANY CORE LAYOUT) --- */}
            {(selectedCreationRole === 'vendor' || selectedCreationRole === 'client') && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Company Name <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Veritas Solutions Ltd"
                    value={formValues.companyName || ''}
                    onChange={(e) => setFormValues({...formValues, companyName: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Authorized Contact Person Name <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Snehashish Roy"
                    value={formValues.authorizedPersonName || ''}
                    onChange={(e) => setFormValues({...formValues, authorizedPersonName: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Authorized Person Contact Number <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="+91 98888 77777"
                    value={formValues.authorizedPersonNumber || ''}
                    onChange={(e) => setFormValues({...formValues, authorizedPersonNumber: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Company Permanent Number</Label>
                  <Input
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="+91 22 2453664"
                    value={formValues.companyPermanentNumber || ''}
                    onChange={(e) => setFormValues({...formValues, companyPermanentNumber: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Authorized Person Email ID <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    type="email"
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="snehashish@veritas.com"
                    value={formValues.authorizedPersonEmail || ''}
                    onChange={(e) => setFormValues({...formValues, authorizedPersonEmail: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Company Official Email ID <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    type="email"
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="info@veritas.com"
                    value={formValues.companyOfficialEmail || ''}
                    onChange={(e) => setFormValues({...formValues, companyOfficialEmail: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">
                    {selectedCreationRole === 'client' ? 'GSTN Number *' : 'GSTN (Optional)'}
                  </Label>
                  <Input
                    required={selectedCreationRole === 'client'}
                    className="bg-slate-50/50 border-slate-200 text-xs font-mono uppercase"
                    placeholder="27AAAAA1111A1Z1"
                    value={formValues.gstn || ''}
                    onChange={(e) => setFormValues({...formValues, gstn: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Company Permanent Address <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Building 4B, Technopark"
                    value={formValues.companyAddress || ''}
                    onChange={(e) => setFormValues({...formValues, companyAddress: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Pincode <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs font-mono"
                    placeholder="400013"
                    value={formValues.pincode || ''}
                    onChange={(e) => setFormValues({...formValues, pincode: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">State <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Maharashtra"
                    value={formValues.state || ''}
                    onChange={(e) => setFormValues({...formValues, state: e.target.value})}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">City <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Mumbai"
                    value={formValues.city || ''}
                    onChange={(e) => setFormValues({...formValues, city: e.target.value})}
                  />
                </div>

                {selectedCreationRole === 'client' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Client Industry Type <span className="text-rose-500">*</span></Label>
                    <Select value={formValues.clientType} onValueChange={(val) => setFormValues({...formValues, clientType: val})}>
                      <SelectTrigger className="bg-slate-50/50 border-slate-200 text-xs h-9">
                        <SelectValue placeholder="Industry type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="Banking">Banking Sector</SelectItem>
                        <SelectItem value="Insurance">Insurance Provider</SelectItem>
                        <SelectItem value="Fintech">Fintech Lending</SelectItem>
                        <SelectItem value="Telecom">Telecom Corporate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-1.5 col-span-1">
                  <Label className="text-xs font-semibold text-slate-700">Legal Agreement Upload <span className="text-rose-500">*</span></Label>
                  <div className="border border-dashed border-slate-200 rounded-lg p-3 bg-slate-50 text-center hover:bg-slate-100 transition cursor-pointer">
                    <Upload className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                    <span className="text-[10px] text-slate-600 font-medium block">Click to upload signed PDF</span>
                  </div>
                </div>

                <div className="space-y-1.5 col-span-1">
                  <Label className="text-xs font-semibold text-slate-700">Company Logo Upload</Label>
                  <div className="border border-dashed border-slate-200 rounded-lg p-3 bg-slate-50 text-center hover:bg-slate-100 transition cursor-pointer">
                    <Upload className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                    <span className="text-[10px] text-slate-600 font-medium block">Click to upload high-res PNG</span>
                  </div>
                </div>
              </>
            )}

            {/* --- BUSINESS PARTNER FORM --- */}
            {selectedCreationRole === 'business_partner' && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Company Name <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Alliance Associates"
                    value={formValues.companyName || ''}
                    onChange={(e) => setFormValues({...formValues, companyName: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Primary Contact Person <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Rajesh Shah"
                    value={formValues.contactPerson || ''}
                    onChange={(e) => setFormValues({...formValues, contactPerson: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Assigned Operations Products <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    className="bg-slate-50/50 border-slate-200 text-xs"
                    placeholder="Address Verification, Credit Checks"
                    value={formValues.assignedProducts || ''}
                    onChange={(e) => setFormValues({...formValues, assignedProducts: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Commission Percentage % <span className="text-rose-500">*</span></Label>
                  <Input
                    required
                    type="number"
                    className="bg-slate-50/50 border-slate-200 text-xs font-mono"
                    placeholder="12"
                    value={formValues.commissionPercentage || ''}
                    onChange={(e) => setFormValues({...formValues, commissionPercentage: e.target.value})}
                  />
                </div>
              </>
            )}

          </div>

          {/* Floating Footer Save Header Bar */}
          <div className="border-t border-slate-200 pt-5 flex items-center justify-between">
            <Button variant="outline" className="border-slate-200 text-slate-700" type="button" onClick={() => setCreationStep(1)}>
              Back
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" className="text-slate-500 hover:text-slate-800" type="button" onClick={() => navigate('/users/all')}>
                Cancel
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow" type="submit">
                Generate User Credentials
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
